/**
 * Reconnect / refresh mutex · spec 05 §L1494-L1530.
 *
 * Mid-session JWT refresh and the connectivity-machine reconnect ladder
 * BOTH need to drive `nc.reconnect()` exclusively. Without serialisation
 * a refresh in flight + a heartbeat-miss-triggered reconnect race —
 * resulting in either two reconnects in lock-step (double-bulk-snapshot
 * 503 from rate-limit) or one cancelling the other (silent stall).
 *
 * The mutex is a simple FIFO queue: callers `await acquire()`, do their
 * work, then call the released `release()` function. Re-entrant locking
 * is NOT permitted — the JWT refresh path and the reconnect path are
 * disjoint by design.
 */

export interface MutexHandle {
  release(): void;
}

export class ReconnectMutex {
  private locked = false;
  private readonly waiters: Array<(handle: MutexHandle) => void> = [];

  async acquire(): Promise<MutexHandle> {
    if (!this.locked) {
      this.locked = true;
      return this.makeHandle();
    }
    return new Promise<MutexHandle>((resolve) => {
      this.waiters.push(resolve);
    });
  }

  private makeHandle(): MutexHandle {
    let released = false;
    return {
      release: (): void => {
        if (released) return;
        released = true;
        const next = this.waiters.shift();
        if (next === undefined) {
          this.locked = false;
          return;
        }
        // Hand the lock to the next waiter — `locked` stays true.
        next(this.makeHandle());
      },
    };
  }

  /** Test-only — drop every queued waiter. */
  _resetForTest(): void {
    this.locked = false;
    this.waiters.length = 0;
  }
}

/** Module-singleton mutex shared by jwt-refresh and the connectivity machine. */
export const reconnectMutex = new ReconnectMutex();

// ─────────────────────────────────────────────────────────────────────────
// T6013 · connectionAction state-machine SHIM · file 05 §L1494-1530
// ─────────────────────────────────────────────────────────────────────────
//
// AUTH-022 / ADR-002 resolution: the authoritative `connectionAction`
// coalescing mutex lives in `connectivity/connection-action.ts` (T16201).
// This file MUST NOT write to `globalThis.__morfi_connectionAction`
// directly — doing so previously duplicated the slot owner and produced
// races with the canonical mutex.
//
// The legacy `tryRefresh` / `transportReconnect` surface stays here for
// in-flight call sites; both functions are thin shims that delegate to
// the canonical mutex API:
//
//   - `tryRefresh` → `requestAction('refresh', perform)` with a state
//     pre-check that returns `'skipped'` when not STABLE.
//   - `transportReconnect` → `requestAction('reconnect', perform)` with
//     a DEGRADED flip on perform failure.
//   - `markDegraded` / `recoverFromDegraded` / `getConnectionAction` /
//     `_resetConnectionActionForTest` forward 1:1 to the canonical
//     module.
//
// New call sites SHOULD import from `connectivity/connection-action.ts`
// directly; this shim is the deprecated entry point retained for the
// pending refactor of `connectivity/jwt-refresh.ts` and the
// `pre-replace-cleanup` HMR hook.

import {
  _resetConnectionActionForTest as _canonicalReset,
  type ConnectionAction as CanonicalConnectionAction,
  getConnectionAction as canonicalGetAction,
  markDegraded as canonicalMarkDegraded,
  requestAction as canonicalRequestAction,
} from '../../connectivity/fsm/connection-action.js';

export type ConnectionAction = CanonicalConnectionAction;

export function getConnectionAction(): ConnectionAction {
  return canonicalGetAction();
}

/**
 * Spec-aligned shorter alias for `getConnectionAction`. Identical
 * semantics; exists because file 05 §L1494-1530 uses the short name.
 */
export function getAction(): ConnectionAction {
  return canonicalGetAction();
}

/**
 * Attempt a JWT refresh. Returns `'skipped'` immediately if the state
 * machine is not STABLE — caller re-arms a short delay (default ≤ 10 s).
 * Returns `'done'` after the inner `perform` resolves (or rejects — see
 * note on errors below).
 *
 * Delegates to the canonical `requestAction('refresh', ...)` mutex
 * (T16201). If `perform()` throws, the canonical mutex returns the slot
 * to STABLE and the error propagates to the caller.
 */
export async function tryRefresh(perform: () => Promise<void>): Promise<'done' | 'skipped'> {
  if (canonicalGetAction() !== 'STABLE') return 'skipped';
  await canonicalRequestAction('refresh', perform);
  return 'done';
}

/**
 * Drive a transport reconnect. Delegates to the canonical
 * `requestAction('reconnect', ...)` mutex (T16201). The canonical mutex
 * coalesces concurrent reconnects and serialises against any in-flight
 * refresh — a queued refresh is implicitly subsumed because the new
 * connect will use the latest cached JWT through the authenticator
 * closure.
 *
 * If `perform()` throws, the state flips to DEGRADED and the caller is
 * expected to surface that via the connectivity machine.
 */
export async function transportReconnect(perform: () => Promise<void>): Promise<void> {
  try {
    await canonicalRequestAction('reconnect', perform);
  } catch (err) {
    canonicalMarkDegraded('transport-reconnect-failed');
    throw err;
  }
}

/**
 * Manually flip to DEGRADED — used by the connectivity machine when
 * the mint endpoint has exhausted its retry ladder and the SDK has
 * decided REST-only is the new normal.
 */
export function markDegraded(): void {
  canonicalMarkDegraded('manual');
}

/**
 * Recover from DEGRADED to STABLE — used after a successful re-mint +
 * reconnect cycle. Idempotent.
 *
 * The canonical `markStable()` refuses to leave DEGRADED (DEGRADED is
 * structurally terminal for in-band callers). The recovery path is
 * out-of-band — it follows a successful re-mint that the connectivity
 * machine arbitrated explicitly — so the shim resets the slot fully
 * and lets the next `requestAction` re-establish STABLE on first call.
 */
export function recoverFromDegraded(): void {
  if (canonicalGetAction() === 'DEGRADED') {
    _canonicalReset();
  }
}

/** Test-only — clear the global pin via the canonical mutex's reset. */
export function _resetConnectionActionForTest(): void {
  _canonicalReset();
}

// ─────────────────────────────────────────────────────────────────────────
// Authenticator closure · file 05 §L1494-1530
// ─────────────────────────────────────────────────────────────────────────
//
// The nats-core client is constructed with `authenticator: () => latestJwtRef.current`
// — NOT `user_jwt: jwt` — so every reconnect attempt, transport-driven
// OR refresh-driven, reads the LATEST cached JWT through the closure.
// Verified bug class across NATS clients: nats.py #193, nats.net #356,
// nats.go #1694. Without this, the FIRST server-initiated close after
// expiry re-uses the now-expired token and burns reconnect attempts
// before the queued refresh fires.

const latestJwtRef: { current: string } = { current: '' };

/** Replace the latest-JWT pin. Called by the refresh path on success. */
export function setLatestJwt(jwt: string): void {
  latestJwtRef.current = jwt;
}

/**
 * Authenticator closure for nats-core. Reads `latestJwtRef.current` on
 * every invocation — nats-core calls it on every reconnect attempt.
 *
 * Named `latestJwt` (not `jwtAuthenticator`) to avoid the collision with
 * the existing `jwtAuthenticator(currentJwt)` factory in `./auth.ts`,
 * which produces a nats-core authenticator callback from a closure.
 * Call this when you want the raw string; call `jwtAuthenticator()`
 * (from `./auth.ts`) when you want the nats-core callback shape.
 */
export function latestJwt(): string {
  return latestJwtRef.current;
}

/** Test-only · reset the JWT pin. */
export function _resetLatestJwtForTest(): void {
  latestJwtRef.current = '';
}

// ─────────────────────────────────────────────────────────────────────────
// Spec-aligned re-export · file 05 §L1494-1530 names the closure
// `jwtAuthenticator`. The factory of the same name in `./auth.ts` builds
// a nats-core authenticator callback from an arbitrary closure; T6013's
// `jwtAuthenticator` (here) is the MODULE-SINGLETON form bound to
// `latestJwtRef.current`. Both shapes coexist; pick by use-case:
//
//   - `jwtAuthenticator(currentJwt)` from `./auth.ts` · pass an explicit
//     reader (e.g. JwtStore.read). Returns a `(nonce?) => { jwt }` callback.
//   - default singleton form (this file) · reads the package-level latest
//     JWT. Returns the raw string for callers that want `() => string`.
//
// Since the names collide, this file re-exports the spec name only as
// part of an explicit object — consumers import as
// `import { authenticatorBoundToLatestJwt } from './reconnect-mutex.js'`
// or by `import * as Mutex` and reach for `Mutex.jwtAuthenticator()`.

export const authenticatorBoundToLatestJwt: () => string = latestJwt;
